import { startOfDay } from "date-fns";
import { useEffect, useState } from "react";

import type {
	CreateMeetingRequestJson,
	CreateRecurringSeriesRequestJson,
	UpdateMeetingRequestJson,
} from "@performyard/service-contracts";

import { CalendarErrorType, VideoConferencingErrorType } from "./types";

import type { MeetingFormSchema } from "./meeting-form-schema";
import {
	type MeetingFormValuesWithOptionalDates,
	areDatesInValidOrder,
	isCalendarErrorRequiringModal,
	isVideoConferencingErrorRequiringModal,
} from "./meeting-form-utils";

export type CalendarErrorContext =
	| { operation: "create"; meetingData: CreateMeetingRequestJson; workspaceId: string }
	| { operation: "update"; meetingData: UpdateMeetingRequestJson; meetingId: string };

export type CalendarErrorModalState =
	| { type: "none" }
	| {
			type: "error";
			errorType: CalendarErrorType;
			context: CalendarErrorContext;
			isRetrying: boolean;
	  };

export const useValidationErrorClearer = (
	formValues: MeetingFormValuesWithOptionalDates,
	validationErrors: Partial<Record<keyof MeetingFormSchema, string>>,
	setValidationErrors: (errors: Partial<Record<keyof MeetingFormSchema, string>>) => void
): void => {
	useEffect(() => {
		if (Object.keys(validationErrors).length > 0) {
			const newErrors = { ...validationErrors };
			let errorsChanged = false;

			if (newErrors.name && formValues.name.trim()) {
				delete newErrors.name;
				errorsChanged = true;
			}

			if (newErrors.description && formValues.description?.trim() && formValues.description.length <= 2000) {
				delete newErrors.description;
				errorsChanged = true;
			}

			// Clear startDate error only if startDate is now present
			if (newErrors.startDate && formValues.startDate) {
				delete newErrors.startDate;
				errorsChanged = true;
			}

			// Clear endDate error only if:
			// 1. The error is about missing endDate and endDate is now present, OR
			// 2. The error is about date ordering and dates are now in valid order
			if (newErrors.endDate && formValues.endDate) {
				// If the error was about date ordering, only clear if dates are now in valid order
				const isDateOrderError = newErrors.endDate.toLowerCase().includes("after");
				if (isDateOrderError) {
					if (areDatesInValidOrder(formValues)) {
						delete newErrors.endDate;
						errorsChanged = true;
					}
				} else {
					// It's a "required field" error, just having the date is enough
					delete newErrors.endDate;
					errorsChanged = true;
				}
			}

			if (newErrors.startTime && formValues.startTime) {
				delete newErrors.startTime;
				errorsChanged = true;
			}

			if (newErrors.endTime && formValues.endTime) {
				delete newErrors.endTime;
				errorsChanged = true;
			}

			if (newErrors.workspaceId && formValues.workspaceId) {
				delete newErrors.workspaceId;
				errorsChanged = true;
			}

			// Mirrors endDate: switching the series back off a date retires the error outright,
			// a "required field" error needs only a date, and the ordering error needs one that
			// no longer precedes the start date.
			if (newErrors.seriesEndDate) {
				if (formValues.seriesEnds !== "onDate") {
					delete newErrors.seriesEndDate;
					errorsChanged = true;
				} else if (formValues.seriesEndDate) {
					const isDateOrderError = newErrors.seriesEndDate.toLowerCase().includes("before");
					const isOrderNowValid =
						!!formValues.startDate && formValues.seriesEndDate >= startOfDay(formValues.startDate);
					if (!isDateOrderError || isOrderNowValid) {
						delete newErrors.seriesEndDate;
						errorsChanged = true;
					}
				}
			}

			if (errorsChanged) {
				setValidationErrors(newErrors);
			}
		}
	}, [formValues, validationErrors, setValidationErrors]);
};

export const useCalendarErrorHandler = (): {
	modalState: CalendarErrorModalState;
	handleCalendarError: (error: unknown, context: CalendarErrorContext) => boolean;
	closeModal: () => void;
	setRetrying: (retrying: boolean) => void;
} => {
	const [modalState, setModalState] = useState<CalendarErrorModalState>({ type: "none" });

	const handleCalendarError = (error: unknown, context: CalendarErrorContext): boolean => {
		const { requiresModal, errorType } = isCalendarErrorRequiringModal(error);

		if (!requiresModal || !errorType) {
			return false;
		}

		setModalState({ type: "error", errorType, context, isRetrying: false });
		return true;
	};

	const closeModal = (): void => {
		setModalState({ type: "none" });
	};

	const setRetrying = (retrying: boolean): void => {
		if (modalState.type === "error") {
			setModalState({ ...modalState, isRetrying: retrying });
		}
	};

	return {
		modalState,
		handleCalendarError,
		closeModal,
		setRetrying,
	};
};

export type VideoConferencingErrorContext =
	| { operation: "create"; meetingData: CreateMeetingRequestJson; workspaceId: string }
	| { operation: "createSeries"; seriesData: CreateRecurringSeriesRequestJson; workspaceId: string }
	| { operation: "update"; meetingData: UpdateMeetingRequestJson; meetingId: string };

export type VideoConferencingErrorModalState =
	| { type: "none" }
	| {
			type: "error";
			errorType: VideoConferencingErrorType;
			context: VideoConferencingErrorContext;
			isRetrying: boolean;
	  };

// Re-export the eligibility hook so modal consumers keep importing from `./hooks`.
// The canonical definition lives in the parent MeetingsTable hooks file alongside
// `useCalendarIntegrationStatus`.
export { useVideoConferencingEligibility } from "../../hooks";

export const useVideoConferencingErrorHandler = (): {
	modalState: VideoConferencingErrorModalState;
	handleVideoConferencingError: (error: unknown, context: VideoConferencingErrorContext) => boolean;
	closeModal: () => void;
	setRetrying: (retrying: boolean) => void;
} => {
	const [modalState, setModalState] = useState<VideoConferencingErrorModalState>({ type: "none" });

	const handleVideoConferencingError = (error: unknown, context: VideoConferencingErrorContext): boolean => {
		const { requiresModal, errorType } = isVideoConferencingErrorRequiringModal(error);
		if (!requiresModal || !errorType) {
			return false;
		}
		setModalState({ type: "error", errorType, context, isRetrying: false });
		return true;
	};

	const closeModal = (): void => {
		setModalState({ type: "none" });
	};

	const setRetrying = (retrying: boolean): void => {
		if (modalState.type === "error") {
			setModalState({ ...modalState, isRetrying: retrying });
		}
	};

	return {
		modalState,
		handleVideoConferencingError,
		closeModal,
		setRetrying,
	};
};

/**
 * Shared retry/continue resubmit for the calendar and conferencing error modals: marks the
 * modal as retrying, resubmits, and closes on success or re-enables the buttons on failure.
 * Callers pass the (possibly stripped) variables for their retry vs continue-without cases.
 */
export const createErrorModalResubmit = <TVariables>(
	mutate: (variables: TVariables, options: { onSuccess: () => void; onError: () => void }) => void,
	setRetrying: (retrying: boolean) => void,
	closeModal: () => void
): ((variables: TVariables) => void) => {
	return (variables) => {
		setRetrying(true);
		mutate(variables, {
			onSuccess: () => closeModal(),
			onError: () => setRetrying(false),
		});
	};
};
